import React, {
	createContext,
	type Dispatch,
	type SetStateAction,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { Virtualizer } from '@tanstack/virtual-core';

type ShouldUseSSRVirtualizerContextType = [boolean, Dispatch<SetStateAction<boolean>>];

const ShouldUseSSRVirtualizerContext = createContext<ShouldUseSSRVirtualizerContextType | null>(
	null,
);

export const ShouldUseSSRVirtualizerProvider = ({ children }: { children: React.ReactNode }) => {
	const value = useState(true);

	return (
		<ShouldUseSSRVirtualizerContext.Provider value={value}>
			{children}
		</ShouldUseSSRVirtualizerContext.Provider>
	);
};

const useShouldUseSSRVirtualizer = () => {
	const context = useContext(ShouldUseSSRVirtualizerContext);

	if (!context) {
		throw new Error(
			'useShouldUseSSRVirtualizer must be used within a ShouldUseSSRVirtualizerProvider',
		);
	}

	return context;
};

type Virtualization = Virtualizer<HTMLElement, Element>;

/**
 * Subset of virtualizer props, for easy simulate for SSR
 */
export type BaseServerVirtualizer = Pick<Virtualization, 'getTotalSize' | 'getVirtualItems'>;

export type ServerVirtualizer = Pick<
	Virtualization,
	'getTotalSize' | 'measureElement' | 'getVirtualItems'
> & {
	options: Pick<Virtualization['options'], 'scrollMargin'>;
	isScrolling: boolean;
};

type UseVirtualizerArgs = Parameters<typeof useVirtualizer<HTMLElement, Element>>[0];

/**
 * Create a virtualizer with an specific number of items to be returned when SSR
 * @param options
 * @returns
 */
export const createServerVirtualizer = (
	options: Pick<UseVirtualizerArgs, 'count' | 'estimateSize' | 'getItemKey' | 'scrollMargin'>,
): BaseServerVirtualizer => {
	const { count, estimateSize, getItemKey } = options;
	const totalSize = count * estimateSize(0);
	let start = 0;
	const items = Array.from({ length: count }).map((_, i) => {
		const end = start + estimateSize(i);
		const item = {
			index: i,
			key: getItemKey?.(i) ?? i,
			start,
			end,
			lane: 0,
			size: estimateSize(i),
		};
		start = end;
		return item;
	});

	return {
		getTotalSize() {
			return totalSize;
		},
		getVirtualItems() {
			return items;
		},
	};
};

const dummyVirtualizer: BaseServerVirtualizer = {
	getTotalSize() {
		return 0;
	},
	getVirtualItems() {
		return [];
	},
};

export const useSSRSafeVirtualizer = (options: UseVirtualizerArgs): ServerVirtualizer => {
	const { getScrollElement, count, estimateSize, getItemKey } = options;
	const [shouldUseSSRVirtualizer, setShouldUseSSRVirtualizer] = useShouldUseSSRVirtualizer();

	const serverVirtualizer = useMemo(() => {
		if (shouldUseSSRVirtualizer) {
			// only calculated when is going to be used
			return createServerVirtualizer({ count, estimateSize, getItemKey });
		}
		return dummyVirtualizer;
	}, [count, estimateSize, getItemKey, shouldUseSSRVirtualizer]);

	const originalVirtualizer: Virtualization = useVirtualizer<HTMLElement, Element>(options);

	/**
	 * We swap to real virtualization when we detect that the user has initiated a scroll
	 */
	useEffect(() => {
		if (!shouldUseSSRVirtualizer) {
			return;
		}

		const element = getScrollElement();

		const onScroll = () => {
			if (!__SERVER__) {
				setShouldUseSSRVirtualizer(false);
				element?.removeEventListener('scroll', onScroll);
			}
		};
		element?.addEventListener('scroll', onScroll);
		return () => {
			element?.removeEventListener('scroll', onScroll);
		};
	}, [shouldUseSSRVirtualizer, getScrollElement, setShouldUseSSRVirtualizer]);

	/**
	 * We overwrite the methods to render the items and calculate size
	 * to assure that SSR renders the same as the frontend on the initial
	 * render
	 */
	return useMemo<ServerVirtualizer>(() => {
		if (shouldUseSSRVirtualizer) {
			return {
				...originalVirtualizer,
				...serverVirtualizer,
			};
		}

		// Ensure we return a stable reference between renders when we stop using the SSR virtualizer
		return originalVirtualizer;
	}, [originalVirtualizer, serverVirtualizer, shouldUseSSRVirtualizer]);
};
